import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill, SearchInput } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, Th, TenancyModal } from '../platform/_ui';
import { teamsApi } from '../api/teams.api.js';
import { usePermissions } from './auth/usePermissions.js';
import { ORG } from './auth/capabilities.js';
import { DeniedState } from './auth/DeniedState.jsx';

const EMPTY_FORM = { name: '', description: '' };
const getTeamMemberIds = (team) => (team.teamMemberships || []).map(m => m.memberId || m);
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export default function OrgTeams({ data }) {
  const { orgId } = useParams();
  const navigate  = useNavigate();
  const [searchParams] = useSearchParams();
  const toast     = useToast();
  const perm      = usePermissions();

  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel') || 'Back';

  const [teams, setTeams] = useState([]);
  const [q, setQ] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [editModal,   setEditModal]   = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setTeams((await teamsApi.list(orgId)) || []); } catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load teams' }); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const org = data.state.organizations.find(o => o.id === orgId);
  if (!org) return null;

  if (!perm.has(ORG.TEAMS_VIEW) && !perm.isSA) return (
    <div className="nx-cc-page">
      <DeniedState reason="insufficient_permission" message="Teams management requires the Teams View capability. Contact your org admin." />
    </div>
  );

  const members = (data.state.members || []).filter(m => m.org === orgId);

  const isAdmin = perm.has(ORG.TEAMS_CREATE) || perm.isSA;
  const filtered = teams.filter(t => !q || t.name.toLowerCase().includes(q.toLowerCase()));
  const totalUniqueMembers = new Set(teams.flatMap(t => getTeamMemberIds(t))).size;

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    try {
      const team = await teamsApi.create(orgId, { name: form.name.trim(), description: form.description.trim() });
      setTeams(ts => [team, ...ts]);
      toast({ tone: 'success', icon: 'Users', title: 'Team created', desc: team.name });
      setCreateModal(false);
      setForm(EMPTY_FORM);
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to create team', desc: e.message });
    }
  };

  const handleEdit = async () => {
    if (!form.name.trim()) return;
    try {
      const updated = await teamsApi.update(orgId, editModal.id, { name: form.name.trim(), description: form.description.trim() });
      setTeams(ts => ts.map(t => t.id === editModal.id ? updated : t));
      toast({ tone: 'success', icon: 'Check', title: 'Team updated' });
      setEditModal(null);
      setForm(EMPTY_FORM);
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to update team', desc: e.message });
    }
  };

  const handleDelete = async () => {
    try {
      await teamsApi.remove(orgId, deleteConfirm.id);
      setTeams(ts => ts.filter(t => t.id !== deleteConfirm.id));
      toast({ tone: 'warning', icon: 'Trash', title: 'Team deleted', desc: deleteConfirm.name });
      setDeleteConfirm(null);
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to delete team', desc: e.message });
    }
  };

  const toggleMember = async (team, memberId) => {
    const has = getTeamMemberIds(team).includes(memberId);
    try {
      if (has) {
        await teamsApi.removeMember(orgId, team.id, memberId);
      } else {
        await teamsApi.addMember(orgId, team.id, memberId);
      }
      const freshTeams = (await teamsApi.list(orgId)) || [];
      setTeams(freshTeams);
      const fresh = freshTeams.find(t => t.id === team.id);
      if (fresh) setAssignModal(fresh);
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to update members', desc: e.message });
    }
  };

  const openEdit = (team) => { setForm({ name: team.name, description: team.description || '' }); setEditModal(team); };
  const openCreate = () => { setForm(EMPTY_FORM); setCreateModal(true); };

  let tableContent;
  if (loading) {
    tableContent = <div className="nx-empty"><NexusIcon name="Loader" size={20} /><span>Loading teams…</span></div>;
  } else if (filtered.length === 0) {
    tableContent = (
      <div className="nx-empty">
        <NexusIcon name="Users" size={24} />
        <span>{q ? 'No teams match your search.' : 'No teams yet. Create the first team.'}</span>
        {!q && <Btn variant="secondary" icon="Plus" onClick={openCreate}>Create Team</Btn>}
      </div>
    );
  } else {
    tableContent = (
      <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
        <thead><tr>
          <Th icon="Users">Team</Th>
          <Th icon="User">Members</Th>
          <Th icon="LayoutGrid">Workspaces</Th>
          <Th icon="Calendar">Created</Th>
          <Th end />
        </tr></thead>
        <tbody>{filtered.map(team => {
          const memberIds = getTeamMemberIds(team);
          const wsCount = (team.teamWorkspaces || []).length;
          return (
            <tr key={team.id} className="nx-tr" style={{ cursor: 'pointer' }} onClick={() => setAssignModal(team)}>
              <td>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{team.name}</div>
                  {team.description && <div className="nx-muted nx-mini">{team.description}</div>}
                </div>
              </td>
              <td><span className="nx-muted">{memberIds.length} members</span></td>
              <td><span className="nx-muted">{wsCount} workspaces</span></td>
              <td><span className="nx-muted nx-mini">{fmt(team.createdAt)}</span></td>
              <td className="nx-td-end" style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                <Btn variant="ghost" size="sm" icon="Users" onClick={() => setAssignModal(team)}>Members</Btn>
                {isAdmin && <Btn variant="ghost" size="sm" icon="Pencil" onClick={() => openEdit(team)}>Edit</Btn>}
                {isAdmin && <Btn variant="ghost" size="sm" icon="Trash"  onClick={() => setDeleteConfirm(team)}>Delete</Btn>}
              </td>
            </tr>
          );
        })}</tbody>
      </table></div></div>
    );
  }

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Teams"
        sub={teams.length + ' teams in ' + org.name}
        back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined}
        actions={isAdmin && <Btn variant="primary" icon="Plus" onClick={openCreate}>Create Team</Btn>}
      />

      <div className="nx-cc-stat-grid nx-kpi-3">
        <StatCard label="Total Teams"   value={teams.length}      icon="Users"     color="blue" />
        <StatCard label="Total Members" value={totalUniqueMembers} icon="User"      color="blue"    />
        <StatCard label="Org Members"   value={members.length}    icon="Building2" color="#6E3FBA" />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
        <SearchInput value={q} onChange={e => setQ(e.target.value)} placeholder="Search teams…" width={280} />
      </div>

      {tableContent}

      {createModal && (
        <TenancyModal title="Create Team" icon="Users" onClose={() => setCreateModal(false)}>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Team Name <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
              <input className="nx-input" autoFocus placeholder="e.g. Sales Team" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }} />
            </div>
            <div className="nx-form-row">
              <label>Description</label>
              <input className="nx-input" placeholder="Optional description" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
          </div>
          {!form.name.trim() && (
            <p style={{ fontSize: 12, color: 'var(--nx-danger)', margin: '8px 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
              <NexusIcon name="AlertCircle" size={12} /> Name is required
            </p>
          )}
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => setCreateModal(false)}>Cancel</Btn>
            <Btn variant="primary" icon="Plus" disabled={!form.name.trim()} onClick={handleCreate}>Create Team</Btn>
          </div>
        </TenancyModal>
      )}

      {editModal && (
        <TenancyModal title={'Edit Team — ' + editModal.name} icon="Pencil" onClose={() => setEditModal(null)}>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Team Name</label>
              <input className="nx-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="nx-form-row">
              <label>Description</label>
              <input className="nx-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => setEditModal(null)}>Cancel</Btn>
            <Btn variant="primary" icon="Check" disabled={!form.name.trim()} onClick={handleEdit}>Save Changes</Btn>
          </div>
        </TenancyModal>
      )}

      {assignModal && (
        <TenancyModal title={'Assign Members — ' + assignModal.name} icon="UserPlus" onClose={() => setAssignModal(null)} width={560}>
          <p className="nx-confirm-desc">{getTeamMemberIds(assignModal).length} of {members.length} org members assigned to this team.</p>
          <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 8 }}>
            {members.length === 0
              ? <span className="nx-muted nx-mini">No org members yet. Invite members first.</span>
              : members.map(m => {
                  const assigned = getTeamMemberIds(assignModal).includes(m.id);
                  return (
                    <div key={m.id} onClick={() => toggleMember(assignModal, m.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', cursor: 'pointer', borderRadius: 6, transition: 'background 0.1s' }}>
                      <div style={{ width: 18, height: 18, borderRadius: 4, border: '2px solid', borderColor: assigned ? 'var(--nx-accent)' : 'var(--nx-border)', background: assigned ? 'var(--nx-accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {assigned && <NexusIcon name="Check" size={10} style={{ color: '#fff' }} />}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{m.name}</span>
                      <span className="nx-muted nx-mini" style={{ flex: 1 }}>{m.email}</span>
                      <Pill tone="gray">{m.orgRole || 'Member'}</Pill>
                    </div>
                  );
                })
            }
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => setAssignModal(null)}>Close</Btn>
          </div>
        </TenancyModal>
      )}

      {deleteConfirm && (
        <TenancyModal title={'Delete Team — ' + deleteConfirm.name + '?'} icon="Trash" danger onClose={() => setDeleteConfirm(null)}>
          <p className="nx-confirm-desc">This permanently deletes the team. Members are not removed from the organization.</p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setDeleteConfirm(null)}>Cancel</Btn>
            <Btn variant="danger" icon="Trash" onClick={handleDelete}>Delete Team</Btn>
          </div>
        </TenancyModal>
      )}
    </div>
  );
}
